import { QuoteDetailData } from '@/lib/api'
import { env } from '@/lib/env'
import { msalInstance } from '@/lib/msal-config'
import { SessionExpiredError } from '@/lib/session-expired-error'
import { setAuthenticatedUser, trackEvent } from '@/lib/telemetry'
import { getUniqueQueryKeys } from '@/lib/utils'
import { AccountInfo, InteractionRequiredAuthError, SilentRequest } from '@azure/msal-browser'
import { useDebouncedValue } from '@mantine/hooks'
import { QueryKey } from '@tanstack/react-query'
import { persistNSync } from 'persist-and-sync'
import { useMemo } from 'react'
import { create } from 'zustand'
import { createJSONStorage, devtools, persist } from 'zustand/middleware'

type AccessToken = { accessToken: string; expiresAt: number }

type TokenState = {
  token: AccessToken | null
  /**
   * Silent renewal needs the user at the sign-in page. Not persisted: the
   * redirect back reloads the tab, and a fresh load must try renewal again.
   */
  sessionExpired: boolean
  /**
   * This page load has seen a signed-in account. Losing it afterwards means the
   * session expired; never having one is first sign-in, which
   * MsalAuthenticationTemplate handles by redirect. Not persisted for the same
   * reason as sessionExpired.
   */
  hadAccount: boolean
  getToken: () => Promise<AccessToken>
  setToken: (token: AccessToken | null) => void
}

function getAccount() {
  return (
    msalInstance.getActiveAccount() ||
    msalInstance.getAllAccounts().at(0) ||
    msalInstance.getAccount({ tenantId: env.VITE_TENANT_ID })
  )
}

const tokenScopes = [`${env.VITE_DYNAMICS_API_BASE_URL}/.default`]

// One renewal shared by every caller: a page load fires checkPricing and
// inventory calls in parallel, and each would otherwise open its own iframe
// and report its own failure.
let pendingToken: Promise<AccessToken> | null = null

/** Raise the banner once per burst: one event, not one per failed call. */
function flagSessionExpired(properties: Record<string, string>) {
  if (useTokenStore.getState().sessionExpired) return
  useTokenStore.setState({ sessionExpired: true })
  trackEvent('auth_session_expired', properties)
}

async function acquireToken(account: AccountInfo): Promise<AccessToken> {
  try {
    const silentRequest: SilentRequest = { scopes: tokenScopes, account }
    const { accessToken, expiresOn } = await msalInstance.acquireTokenSilent(silentRequest)
    const newToken = {
      accessToken,
      expiresAt: expiresOn!.getTime() - 60000, // Expire 1 minute early to be safe
    }
    useTokenStore.setState({ token: newToken })
    return newToken
  } catch (error) {
    if (!(error instanceof InteractionRequiredAuthError)) throw error
    // No automatic redirect: it would throw away unsaved edits. The banner
    // lets the user copy their work first, then sign in on their own terms.
    flagSessionExpired({ errorCode: error.errorCode, subError: error.subError })
    throw new SessionExpiredError(error)
  }
}

/** Interactive renewal from the session-expired banner; returns to the same quote. */
export async function signInAgain() {
  await msalInstance.acquireTokenRedirect({
    scopes: tokenScopes,
    account: getAccount() ?? undefined,
    state: window.location.search,
  })
}

export const useTokenStore = create<TokenState>()(
  persist(
    devtools((set, get) => ({
      token: null,
      sessionExpired: false,
      hadAccount: false,
      getToken: async () => {
        // Initialize MSAL instance if not already initialized
        await msalInstance.initialize()

        // Get active account
        const activeAccount = getAccount()
        if (!activeAccount) {
          if (!get().hadAccount) throw new Error('No users signed in.')
          // An open tab whose MSAL cache lost the account: background reads
          // swallow errors, so without the banner they just show empty data.
          flagSessionExpired({ errorCode: 'no_account' })
          throw new SessionExpiredError()
        }
        if (!get().hadAccount) set({ hadAccount: true })

        // Set authenticated user context for App Insights (covers returning sessions)
        setAuthenticatedUser(activeAccount.localAccountId, activeAccount.tenantId)

        // Check if token is already cached and not expired
        const existingToken = get().token
        if (existingToken && Date.now() < existingToken.expiresAt) {
          return existingToken
        }

        // Silent renewal already failed for good; only the banner's redirect fixes it.
        if (get().sessionExpired) throw new SessionExpiredError()

        pendingToken ??= acquireToken(activeAccount).finally(() => {
          pendingToken = null
        })
        return pendingToken
      },
      setToken: (token) => set({ token }),
    })),
    {
      name: 'token-cache-storage',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({ token: state.token }),
    }
  )
)

type CollapsedState = {
  [key: string]: boolean
}

interface ViewState {
  currentRoom: string | null
  setCurrentRoom: (currentRoom: string | null) => void
  currentSystem: number | null
  setCurrentSystem: (currentSystem: number | null) => void
  currentPhase: number | null
  setCurrentPhase: (currentPhase: number | null) => void
  search: string | null
  setSearch: (search: string | null) => void
  temporaryRoomToSystems: {
    [roomId: string]: number[]
  }
  addTemporaryRoomToSystems: (roomAndSystem: { roomId: string; systemId: number }) => void
  removeTemporaryRoom: (roomId: string) => void
  removeTemporaryRoomToSystems: (roomAndSystem: { roomId: string; systemId: number }) => void
  expandedRooms: CollapsedState
  toggleRoom: (params: { roomId: string; action?: 'collapse' | 'expand' }) => void
  expandedSystems: CollapsedState
  toggleSystem: (params: { roomId: string; systemId: number; action?: 'collapse' | 'expand' }) => void
  expandedScopeOfWorks: CollapsedState
  toggleScopeOfWork: (params: { roomId: string; systemId?: number | null; action?: 'collapse' | 'expand' }) => void
}

export const useViewState = create<ViewState>()(
  devtools((set) => ({
    currentRoom: null,
    setCurrentRoom: (currentRoom) => set({ currentRoom }),
    currentSystem: null,
    setCurrentSystem: (currentSystem) => set({ currentSystem }),
    currentPhase: null,
    setCurrentPhase: (currentPhase) => set({ currentPhase }),
    search: null,
    setSearch: (search) => set({ search: search ?? null }),
    temporaryRoomToSystems: {},
    removeTemporaryRoom: (roomId) =>
      set((state) => {
        if (!state.temporaryRoomToSystems[roomId]) return {}
        // New object, not an in-place delete: subscribers compare by reference,
        // so mutating the existing map leaves the removed room's rows on screen.
        const temporaryRoomToSystems = { ...state.temporaryRoomToSystems }
        delete temporaryRoomToSystems[roomId]
        return { temporaryRoomToSystems }
      }),
    addTemporaryRoomToSystems: ({ roomId, systemId }) =>
      set((state) => {
        const key = getExpandedKey({ roomId, systemId })
        const existingSystems = state.temporaryRoomToSystems[roomId] || []
        const systems = Array.from(new Set(existingSystems.concat(systemId)))
        return {
          temporaryRoomToSystems: {
            ...state.temporaryRoomToSystems,
            [roomId]: systems,
          },
          expandedSystems: {
            ...state.expandedSystems,
            [key]: true,
          },
        }
      }),
    removeTemporaryRoomToSystems: ({ roomId, systemId }) =>
      set((state) => {
        const systems = state.temporaryRoomToSystems[roomId].filter((id) => id !== systemId)
        return {
          temporaryRoomToSystems: { ...state.temporaryRoomToSystems, [roomId]: systems },
        }
      }),
    expandedRooms: {},
    toggleRoom: ({ roomId, action }) => {
      const key = getExpandedKey({ roomId })
      return set((state) => ({
        expandedRooms: {
          ...state.expandedRooms,
          [key]: action ? action === 'expand' : !state.expandedRooms[key],
        },
      }))
    },
    expandedSystems: {},
    toggleSystem: ({ roomId, systemId, action }) => {
      const key = getExpandedKey({ roomId, systemId })
      return set((state) => ({
        expandedSystems: {
          ...state.expandedSystems,
          [key]: action ? action === 'expand' : !state.expandedSystems[key],
        },
      }))
    },
    expandedScopeOfWorks: {},
    toggleScopeOfWork: ({ roomId, systemId, action }) => {
      const key = getExpandedKey({ roomId, systemId })
      return set((state) => ({
        expandedScopeOfWorks: {
          ...state.expandedScopeOfWorks,
          [key]: action ? action === 'expand' : !state.expandedScopeOfWorks[key],
        },
      }))
    },
  }))
)

type ProposalDisplayPanelState = {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
}

export const useProposalDisplayPanel = create<ProposalDisplayPanelState>()((set) => ({
  isOpen: false,
  setIsOpen: (isOpen) => set({ isOpen }),
}))

export const useDebouncedSearch = (delay: number = 500) => {
  const search = useViewState((state) => state.search)
  const [debouncedSearch] = useDebouncedValue(search, delay)
  return useMemo(() => debouncedSearch, [debouncedSearch])
}

export function getExpandedKey({ roomId, systemId }: { roomId: string; systemId?: number | null }) {
  let key = roomId
  if (systemId) {
    key += `-${systemId}`
  }
  return key
}

interface AddAccessoriesDialogState {
  quoteDetailId: string | null
  openDialog: (quoteDetailId: string | null) => void
  closeDialog: () => void
}

export const useAddAccessoriesDialogStore = create<AddAccessoriesDialogState>()(
  devtools((set) => ({
    quoteDetailId: null,
    openDialog: (quoteDetailId) => set({ quoteDetailId }),
    closeDialog: () => set({ quoteDetailId: null }),
  }))
)

/** The section "New package..." was opened from — the pool of items it can take. */
export type PackageCreateScope = {
  roomId: string
  /** Fixed by the system kebab. Null from the room kebab: the first checked item picks it. */
  systemId: number | null
}

// Package dialog state lives here, not in the menus that open it: the dialogs
// are mounted by a component that never unmounts, so closing one can never rip
// its rich-text editor out of the tree mid-teardown.
type PackageDialogState = {
  createScope: PackageCreateScope | null
  openCreatePackage: (scope: PackageCreateScope) => void
  closeCreatePackage: () => void
  /** quotedetailid of the package header being renamed, or null when closed. */
  editingPackageHeaderId: string | null
  setEditingPackageHeaderId: (quoteDetailId: string | null) => void
  /** quotedetailid of the package header being promoted to the catalog, or null. */
  savingToCatalogHeaderId: string | null
  setSavingToCatalogHeaderId: (quoteDetailId: string | null) => void
  /**
   * Where focus goes when a package dialog closes. These dialogs are opened
   * from a store action rather than a Radix trigger, so Radix has nothing to
   * return focus to and Escape drops the caret on document.body.
   *
   * A live DOM node, deliberately: it is ephemeral UI state that never
   * outlives the dialog, and this store is not persisted.
   */
  focusReturn: HTMLElement | null
  /** Remember the element that opened the dialog. Defaults to whatever holds focus now. */
  stashFocusReturn: (element?: HTMLElement | null) => void
  clearFocusReturn: () => void
}

export const usePackageDialogStore = create<PackageDialogState>()(
  devtools((set) => ({
    createScope: null,
    openCreatePackage: (createScope) => set({ createScope }),
    closeCreatePackage: () => set({ createScope: null }),
    editingPackageHeaderId: null,
    setEditingPackageHeaderId: (editingPackageHeaderId) => set({ editingPackageHeaderId }),
    savingToCatalogHeaderId: null,
    setSavingToCatalogHeaderId: (savingToCatalogHeaderId) => set({ savingToCatalogHeaderId }),
    focusReturn: null,
    stashFocusReturn: (element) =>
      set({
        focusReturn:
          element ??
          (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null),
      }),
    clearFocusReturn: () => set({ focusReturn: null }),
  }))
)

export type BulkAction = {
  type: 'edit' | 'replace' | 'delete'
  /**
   * The lines the action covers, as they stood when the menu item was picked.
   * A snapshot on purpose: the writes are optimistic, so the live rows leave the
   * view (deleted, or moved room) while their dialog is still closing.
   */
  quoteDetails: QuoteDetailData[]
}

// Same reason as the package dialogs: opened from a row menu, hosted above the
// tables, so a bulk write that unmounts the row cannot take its dialog with it.
type BulkActionDialogState = {
  /** Kept on close so the dialog's exit animation keeps its content. */
  bulkAction: BulkAction | null
  isBulkActionOpen: boolean
  openBulkAction: (bulkAction: BulkAction) => void
  closeBulkAction: () => void
}

export const useBulkActionDialogStore = create<BulkActionDialogState>()(
  devtools((set) => ({
    bulkAction: null,
    isBulkActionOpen: false,
    openBulkAction: (bulkAction) => set({ bulkAction, isBulkActionOpen: true }),
    closeBulkAction: () => set({ isBulkActionOpen: false }),
  }))
)

interface CopyQuoteDetailState {
  copiedQuoteDetails: QuoteDetailData[]
  setCopiedQuoteDetails: (quoteDetails: QuoteDetailData[]) => void
  clearCopiedQuoteDetails: () => void
}

export const useCopyQuoteDetailStore = create<CopyQuoteDetailState>()(
  devtools(
    persistNSync(
      (set) => ({
        copiedQuoteDetails: [],
        setCopiedQuoteDetails: (quoteDetails: QuoteDetailData[]) => set({ copiedQuoteDetails: quoteDetails }),
        clearCopiedQuoteDetails: () => set({ copiedQuoteDetails: [] }),
      }),
      { name: 'copy-quote-detail-storage', storage: 'localStorage' }
    )
  )
)

type DisplayPreferencesState = {
  isCompact: boolean
  toggleCompact: () => void
  setCompact: (isCompact: boolean) => void
  hideEmptyRooms: boolean
  toggleHideEmptyRooms: () => void
  setHideEmptyRooms: (hideEmptyRooms: boolean) => void
  lastRoomId: string | null
  lastSystemId: number | null
  setLastRoomSystem: (params: { roomId?: string | null; systemId?: number | null }) => void
  hasSeenViewOptions: boolean
  markViewOptionsSeen: () => void
  // Keyed by the header's dsy_globalid — a package owns its members wherever
  // they sit, so unlike rooms and systems the key carries no bucket. Persisted
  // with the other display preferences: a builder who collapsed 40 packages to
  // read a quote gets them back collapsed on the next load.
  expandedPackages: CollapsedState
  togglePackage: (params: { packageGlobalId: string; action?: 'collapse' | 'expand' }) => void
  /** Expand or collapse a whole set at once — the section-level expand/collapse-all. */
  setPackagesExpanded: (globalIds: string[], expanded: boolean) => void
}

/**
 * How many packages' expansion the display preferences remember.
 *
 * The map is keyed by header global id and persisted, so without a bound it
 * grows by one entry per package ever touched, on every quote, forever.
 */
const EXPANDED_PACKAGES_LIMIT = 300

/**
 * The most recently touched entries, oldest dropped.
 *
 * Object keys keep insertion order, and every writer re-inserts the ids it
 * touches, so "oldest" is genuinely least-recently-used. A pruned-away package
 * is not a lost setting so much as a forgotten one: its next read falls back to
 * the default (expanded), exactly as it did before it was ever touched.
 */
function pruneExpandedPackages(expandedPackages: CollapsedState): CollapsedState {
  const globalIds = Object.keys(expandedPackages)
  if (globalIds.length <= EXPANDED_PACKAGES_LIMIT) return expandedPackages
  const pruned: CollapsedState = {}
  for (const globalId of globalIds.slice(globalIds.length - EXPANDED_PACKAGES_LIMIT)) {
    pruned[globalId] = expandedPackages[globalId]
  }
  return pruned
}

export const useDisplayPreferencesStore = create<DisplayPreferencesState>()(
  devtools(
    persist(
      (set) => ({
        isCompact: false,
        toggleCompact: () => set((s) => ({ isCompact: !s.isCompact })),
        setCompact: (isCompact) => set({ isCompact }),
        hideEmptyRooms: true,
        toggleHideEmptyRooms: () => set((s) => ({ hideEmptyRooms: !s.hideEmptyRooms })),
        setHideEmptyRooms: (hideEmptyRooms) => set({ hideEmptyRooms }),
        lastRoomId: null,
        lastSystemId: null,
        setLastRoomSystem: ({ roomId, systemId }) =>
          set((s) => ({
            lastRoomId: roomId !== undefined ? roomId : s.lastRoomId,
            lastSystemId: systemId !== undefined ? systemId : s.lastSystemId,
          })),
        hasSeenViewOptions: false,
        markViewOptionsSeen: () => set({ hasSeenViewOptions: true }),
        expandedPackages: {},
        togglePackage: ({ packageGlobalId, action }) =>
          set((state) => {
            // Re-inserted rather than written in place, so the touched package is
            // the newest entry and the prune drops the ones nobody has opened.
            const { [packageGlobalId]: previous, ...rest } = state.expandedPackages
            return {
              expandedPackages: pruneExpandedPackages({
                ...rest,
                // Compared against false, not negated: rooms and scopes default to
                // COLLAPSED, but a package defaults to EXPANDED (useIsPackageExpanded
                // reads `!== false`), so `!undefined` would write "expanded" onto an
                // already-expanded package and swallow the first click on its chevron.
                [packageGlobalId]: action ? action === 'expand' : previous === false,
              }),
            }
          }),
        setPackagesExpanded: (globalIds, expanded) =>
          set((state) => {
            if (!globalIds.length) return {}
            const expandedPackages = { ...state.expandedPackages }
            for (const globalId of globalIds) delete expandedPackages[globalId]
            for (const globalId of globalIds) expandedPackages[globalId] = expanded
            return { expandedPackages: pruneExpandedPackages(expandedPackages) }
          }),
      }),
      {
        name: 'display-preferences-storage',
        storage: createJSONStorage(() => localStorage),
        // Storage written before the bound existed can hold any number of them.
        merge: (persisted, current) => {
          const persistedState = (persisted ?? {}) as Partial<DisplayPreferencesState>
          return {
            ...current,
            ...persistedState,
            expandedPackages: pruneExpandedPackages(persistedState.expandedPackages ?? current.expandedPackages),
          }
        },
      }
    )
  )
)

type PluginQueryKeysToInvalidateState = {
  queryKeys: QueryKey[]
  addQueryKeys: (queryKeys: QueryKey[]) => void
  removeQueryKeys: (queryKeys: QueryKey[]) => void
  clearQueryKeys: () => void
}

export const usePluginQueryKeysToInvalidateStore = create<PluginQueryKeysToInvalidateState>()(
  devtools((set) => ({
    queryKeys: [],
    addQueryKeys: (queryKeys) => set((prev) => ({ queryKeys: getUniqueQueryKeys(prev.queryKeys.concat(queryKeys)) })),
    removeQueryKeys: (queryKeys) =>
      set((prev) => {
        const removed = new Set(queryKeys.map((k) => JSON.stringify(k)))
        return { queryKeys: prev.queryKeys.filter((k) => !removed.has(JSON.stringify(k))) }
      }),
    clearQueryKeys: () => set({ queryKeys: [] }),
  }))
)
